import * as React from 'react';
import styled from 'styled-components';
import {useNavigate} from '../../../router/compat';
import {TicketDetailBySlugQuery, useTicketDetailBySlugFetchKey} from '../hooks/useTicketDetailBySlug';
import {useTicketUpdate} from '../hooks/useTicketUpdate';
import {useDeleteTicket} from '../hooks/useDeleteTicket';
import {ForecastQueryRenderer} from '../../../graphql/forecastRelay';
import {TicketDetailSkeleton} from '../components/TicketDetailSkeleton';
import {TicketNotFound} from '../components/TicketNotFound';
import {TicketDetailHeader} from '../components/TicketDetailHeader';
import {TicketMetaPanel} from '../components/TicketMetaPanel';
import {OverviewTab} from '../components/OverviewTab';
import {ActivityTab} from '../components/ActivityTab';
import {StreamTab} from '../components/StreamTab';
import {WatchersList} from '../components/WatchersList';
import {RelatedTab} from '../components/RelatedTab';
import {AddLinkModal} from '../components/AddLinkModal';
import {DeleteTicketModal} from '../components/DeleteTicketModal';
import {LinkedTasksTab} from '../components/LinkedTasksTab';
import {TimeTab} from '../components/TimeTab';
import {AddForecastTaskModal} from '../components/AddForecastTaskModal';
import {TicketAttachmentsTab} from '../components/TicketAttachmentsTab';
import {useAttachmentUpload} from '../hooks/useAttachmentUpload';
import {useOpenNativeTaskModal} from '../hooks/useOpenNativeTaskModal';
import {useAuth} from '../../auth/context/AuthContext';
import {hasTicketPerm, TICKET_PERMS} from '../../auth/utils/ticketPerms';
import type {useTicketDetailBySlugQuery$data} from '../hooks/__generated__/useTicketDetailBySlugQuery.graphql';
import type {Ticket} from '../types/ticket';

const MainNarrow = styled.main`
	margin: 0 auto;
	max-width: 64rem;
	padding: 32px 24px;
`;
const MainWide = styled.main`
	margin: 0 auto;
	max-width: 72rem;
	padding: 24px;
`;
const ErrorBox = styled.div`
	border-radius: 6px;
	border: 1px solid #fecaca;
	background: #fef2f2;
	padding: 16px;
`;
const ErrorAlert = styled.div`
	margin-bottom: 16px;
	border-radius: 6px;
	border: 1px solid #fecaca;
	background: #fef2f2;
	padding: 12px;
`;
const ErrorText = styled.p`
	font: ${p => p.theme.ticketing.font.body2};
	color: #b91c1c;
	margin: 0;
`;
const DetailGrid = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1fr);
	gap: 24px;
	@media (min-width: 1024px) {
		grid-template-columns: 1fr 20rem;
	}
`;
const MainColumn = styled.section`
	> * + * {
		margin-top: 20px;
	}
`;
const SideColumn = styled.div`
	> * + * {
		margin-top: 16px;
	}
`;

interface ErrorBoundaryState {
	error: Error | null;
}

class RouteErrorBoundary extends React.Component<{children: React.ReactNode}, ErrorBoundaryState> {
	override state: ErrorBoundaryState = {error: null};
	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return {error};
	}
	override render() {
		if (this.state.error) {
			return (
				<MainNarrow>
					<ErrorBox role="alert">
						<ErrorText>Error: {this.state.error.message}</ErrorText>
					</ErrorBox>
				</MainNarrow>
			);
		}
		return this.props.children;
	}
}

type TicketDetailData = NonNullable<NonNullable<useTicketDetailBySlugQuery$data['viewer']>['ticketDetailBySlug']>;
type TicketRef = TicketDetailData['ticket'];
type Timeline = TicketDetailData['timeline'];
type Attachments = TicketDetailData['attachments'];

function toRestTicket(t: TicketRef): Ticket {
	return {
		id: t.id,
		globalId: t.id,
		slug: t.slug,
		account_id: '',
		requester_id: t.requester?.id ?? '',
		assignee_id: t.assigneeId ?? null,
		subject: t.subject,
		description: t.description ?? null,
		status: t.status as Ticket['status'],
		priority: t.priority as Ticket['priority'],
		channel: (t.channel ?? 'manual') as Ticket['channel'],
		created_at: t.createdAt as string,
		updated_at: t.updatedAt as string,
		closed_at: (t.closedAt as string | null) ?? null,
		last_customer_message_at: null,
		requester_name: t.requester?.name ?? null,
		requester_email: t.requester?.email ?? null,
		assignee_name: t.assignee?.fullName ?? null,
		labels: [],
		sla: null,
	};
}

function toTabAttachments(attachments: Attachments) {
	return attachments.map(a => ({
		id: a.id,
		filename: a.filename,
		contentType: a.contentType,
		sizeBytes: a.sizeBytes,
		downloadUrl: a.downloadUrl,
		createdAt: a.createdAt as string,
		// Prefer the resolved Forecast Person's name; fall back to the requester
		// name for email attachments with no Forecast person (ACC-545).
		uploadedByName: a.uploadedBy?.fullName ?? a.uploadedByName ?? null,
	}));
}

interface ContentProps {
	ticketRef: TicketRef;
	timeline: Timeline;
	attachments: Attachments;
	refetch: () => void;
	currentPersonId: string;
}

function TicketDetailBySlugContent({ticketRef, timeline, attachments, refetch, currentPersonId}: ContentProps) {
	const navigate = useNavigate();
	const openNativeTaskModal = useOpenNativeTaskModal();
	const [activeTab, setActiveTab] = React.useState('overview');
	const [addLinkOpen, setAddLinkOpen] = React.useState(false);
	const [addTaskOpen, setAddTaskOpen] = React.useState(false);
	const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
	const {user} = useAuth();
	const canDelete = !!user && hasTicketPerm(user.ticketPerms, TICKET_PERMS.MANAGE);
	const {ticket, saving, error, updateField} = useTicketUpdate(toRestTicket(ticketRef), refetch);
	const {submit: deleteTicket, loading: deleting} = useDeleteTicket();
	const attachmentUpload = useAttachmentUpload();

	const handleUploadFile = React.useCallback(
		async (file: File) => {
			await attachmentUpload.upload(ticketRef.id, file);
			refetch();
		},
		[attachmentUpload, ticketRef.id, refetch]
	);

	const handleResolve = React.useCallback(() => {
		updateField('status', 'resolved');
	}, [updateField]);

	const handleDeleteConfirm = React.useCallback(() => {
		deleteTicket(ticketRef.id).then(
			() => {
				navigate('/tickets');
			},
			() => {
				setDeleteModalOpen(false);
			}
		);
	}, [deleteTicket, ticketRef.id, navigate]);

	// The Related tab only renders related/child/project links; forecast_task links live in the Tasks tab.
	// Keep the badge in sync with what the tab actually shows.
	const linkCount = ticketRef.links?.filter(l => l.linkType !== 'forecast_task').length ?? 0;
	const timeEntryCount = ticketRef.linkedTasks?.reduce((sum, t) => sum + (t.timeRegistrations?.edges?.length ?? 0), 0) ?? 0;

	const existingLinkTargetIds = (ticketRef.links ?? [])
		.map(l => l.targetTicket?.id ?? l.targetProject?.id)
		.filter((id): id is string => id != null);

	const tabAttachments = React.useMemo(() => toTabAttachments(attachments), [attachments]);

	return (
		<MainWide>
			<TicketDetailHeader
				ticket={ticketRef}
				activeTab={activeTab}
				onTabChange={setActiveTab}
				linkCount={linkCount}
				timeEntryCount={timeEntryCount}
				onResolve={handleResolve}
				onDelete={canDelete ? () => setDeleteModalOpen(true) : undefined}
				saving={saving}
			/>
			{error && (
				<ErrorAlert role="alert">
					<ErrorText>{error}</ErrorText>
				</ErrorAlert>
			)}
			<DetailGrid>
				<MainColumn>
					{activeTab === 'related' ? (
						<RelatedTab ticket={ticketRef} onAddLink={() => setAddLinkOpen(true)} onRefetch={refetch} />
					) : activeTab === 'tasks' ? (
						<LinkedTasksTab
							ticket={ticketRef}
							onAddTask={() => setAddTaskOpen(true)}
							currentPersonId={currentPersonId}
							onRefetch={refetch}
						/>
					) : activeTab === 'time' ? (
						<TimeTab ticket={ticketRef} currentPersonId={currentPersonId} onRefetch={refetch} />
					) : activeTab === 'attachments' ? (
						<TicketAttachmentsTab attachments={tabAttachments} onUploadFile={handleUploadFile} />
					) : activeTab === 'activity' ? (
						<ActivityTab entries={timeline} />
					) : activeTab === 'stream' ? (
						<StreamTab
							entries={timeline}
							ticketId={ticket.id}
							requesterEmail={ticket.requester_email ?? ''}
							onRefetch={refetch}
						/>
					) : (
						<OverviewTab
							ticketRef={ticketRef}
							description={ticket.description}
							timeline={timeline}
							ticketId={ticket.id}
							requesterEmail={ticket.requester_email ?? ''}
							onRefetch={refetch}
							onNavigateToActivity={() => setActiveTab('activity')}
						/>
					)}
				</MainColumn>
				<SideColumn>
					<TicketMetaPanel
						ticket={ticketRef}
						saving={saving}
						onUpdateField={updateField as (field: string, value: unknown) => void}
					/>
					<WatchersList ticket={ticketRef} />
				</SideColumn>
			</DetailGrid>
			<AddLinkModal
				ticketId={ticketRef.id}
				existingLinkTargetIds={existingLinkTargetIds}
				open={addLinkOpen}
				onClose={() => setAddLinkOpen(false)}
				onSuccess={refetch}
			/>
			<AddForecastTaskModal
				ticketId={ticketRef.id}
				open={addTaskOpen}
				onClose={() => setAddTaskOpen(false)}
				onSuccess={refetch}
				onTaskCreated={openNativeTaskModal}
			/>
			<DeleteTicketModal
				open={deleteModalOpen}
				ticketSlug={ticketRef.slug}
				loading={deleting}
				onConfirm={handleDeleteConfirm}
				onCancel={() => setDeleteModalOpen(false)}
			/>
		</MainWide>
	);
}

export function TicketDetailBySlugRoute({slug}: {slug: string}) {
	const {fetchKey, refetch} = useTicketDetailBySlugFetchKey();

	return (
		<RouteErrorBoundary key={fetchKey}>
			<ForecastQueryRenderer
				query={TicketDetailBySlugQuery}
				variables={{slug}}
				customLoader={() => (
					<MainNarrow>
						<TicketDetailSkeleton />
					</MainNarrow>
				)}
				redirectIfNotLoggedIn
				render={(relayProps: useTicketDetailBySlugQuery$data | null) => {
					if (!relayProps || !relayProps.viewer) return null;
					const detail = relayProps.viewer.ticketDetailBySlug;
					if (!detail) {
						return (
							<MainNarrow>
								<TicketNotFound />
							</MainNarrow>
						);
					}
					return (
						<TicketDetailBySlugContent
							ticketRef={detail.ticket}
							timeline={detail.timeline}
							attachments={detail.attachments}
							refetch={refetch}
							currentPersonId={relayProps.viewer.actualPersonId ?? ''}
						/>
					);
				}}
			/>
		</RouteErrorBoundary>
	);
}
